import React, {useEffect, useState, useContext } from 'react';
import styles from './EledoTemplateList.module.scss';
import history from "myHistory";
import PropTypes from "prop-types";
import {
  fetchPrivateTemplates,
  fetchPublicTemplates,
} from "_reducers/templateReducer";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {useErrorNotyf, useShowMenu, usePagination} from "hooks";
import { EledoTemplateListCard } from "components";
import {
  EledoMainPageTitle, EledoLoader, EledoError
} from "components/library";
import ReactTooltip from "react-tooltip";
import { AuthContext } from 'AuthContext';
import { PlatformContext } from 'PlatformContext';
import { useAppDispatch } from '_store';
import { activateRequestsByEvent } from '_reducers/puzzleRequests';

const EledoTemplateList = ({isPublic}) => {

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(activateRequestsByEvent("enter_template_list"));
    return () => dispatch(activateRequestsByEvent("leave_template_list"));
  }, [dispatch])

  useShowMenu();

  const handleCreate = () => {
    history.push("/templates/new");
  };

  return (
    <div className={styles.EledoTemplateList} data-testid="EledoTemplateList">
      {
        isPublic ?
          <EledoMainPageTitle
            title="Public Templates"
            subtitle="Get inspired by our public templates! You can copy any template and edit as your own."
            image="IllustrationsTemplatesV2-01.svg"
          /> :
          <EledoMainPageTitle
            title="My Templates"
            subtitle="Create new templates from a blank canvas, or edit existing templates here."
            image="IllustrationsTemplatesV2-01.svg"
          />
      }

      <TemplateListCards isPublic={isPublic} onCreate={handleCreate} />

    </div>
  );
};

const TemplateListCards = ({isPublic, onCreate}) => {

  const platform = useContext(PlatformContext);
  const auth = useContext(AuthContext);
  const connectionId = auth.connection?.id;
  const [dataDownloaded, setDataDownloaded] = useState(false);
  const [
    templates, itemsTotal, selectedPage,
    loading, error
  ] = useSelector(state => [
    isPublic ? state.templates.public.list : state.templates.private.list,
    isPublic ? state.templates.public.total : state.templates.private.total,
    isPublic ? state.templates.public.selectedPage : state.templates.private.selectedPage,
    state.templates.templatesLoading,
    state.templates.error
  ], shallowEqual);
  const dispatch = useDispatch();

  const fetchData = (pageId) => {
    if (isPublic) {
      dispatch(fetchPublicTemplates({pageId, platform: platform.platform})).then(() => {
        setDataDownloaded(true);
      });
    } else {
      dispatch(fetchPrivateTemplates({pageId, connectionId: connectionId})).then(() => {
        setDataDownloaded(true);
      });
    }
  };

  const renderPagination = usePagination(selectedPage, itemsTotal, fetchData, () => {});

  useEffect(() => {
    fetchData(selectedPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isPublic, connectionId, selectedPage]);

  useErrorNotyf(error);
  
  const renderError = () =>
    <EledoError title={error?.message}
      description={(<React.Fragment>
        We are sorry &#9785; We could not load your templates. <br/><br/>
        {error?.message?.toLocaleLowerCase() === "network error" && "Please check your internet connection."}
    </React.Fragment>)}/>;

  if(isPublic && templates.length === 0) {
    return <div className="flex-center">There are no public templates here.</div>;
  }

  return (
    <div className="section my-content-section" style={{paddingTop: 0}}>

      {error && renderError()}

      {renderPagination()}

      <div className="container" style={{overflowY: "auto"}}>
        {loading && <div className="flex-center"><EledoLoader/></div>}

        {!error && !loading && dataDownloaded &&

          <div className="w-layout-grid template-grid">
            {templates.map((t, index) => (
              <EledoTemplateListCard template={t}
                                    isPublic={isPublic}
                                    key={t.id}/>
            ))}
            <ReactTooltip
              id="copy-template-button-tooltip"
              place="top"
              type="dark"
              effect="float"
              multiline={false}
            />
            <ReactTooltip
              id="edit-template-button-tooltip"
              className={styles.EditTooltip}
              place="top"
              type="dark"
              effect="float"
              multiline={false}
            />
            <ReactTooltip
              id="delete-template-button-tooltip"
              place="top"
              type="dark"
              effect="float"
              multiline={false}
            />
            {!isPublic && <CreateTemplateButton onCreate={onCreate} animationDelay={(templates.length * 100)}/>}
          </div>
        }
      </div>

      {!error && !loading && renderPagination()}

    </div>
  );
};
TemplateListCards.propTypes = {
  isPublic: PropTypes.bool,
};

const CreateTemplateButton = ({onCreate, animationDelay}) => {
  return (
    <button onClick={onCreate}
            className="snow-card centred-all padded-80 new-template w-inline-block template-card-wrapper"
            style={{animationDelay: animationDelay + "ms"}}>
      <img className="new-icon" alt="" width="40" loading="lazy"
           src={`${process.env.PUBLIC_URL}/assets/images/Eledo-File-Icon-Jungle-Plus-01.svg`}/>
      <div>Create a new<br/>document template</div>
    </button>
  )
}

export default EledoTemplateList;
